import { useAppSelector } from "@/core/root-reducer"
import populationStore from "@/entities/population/model/population.store"
import { getRiskFamilyImageUrl } from "@/entities/risk-family/model/risk-family.utils"
import siteStore from "@/entities/site/model/site.store"
import { useNavigate, useParams } from "react-router-dom"
import { PhysicalRisk } from "../model/physical-risk.types"
import { PhysicalRiskMeter } from "./PhysicalRiskMeter"
import UnitChip from "@/components/UnitChip"
import PeopleCount from "@/components/PeopleCount"
import Link from "@/components/link"
import { faPencilAlt, faPlus } from "@fortawesome/free-solid-svg-icons"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import { useRisksSpace } from "@/core/risks-space.context"

interface Props {
  isSummary?: boolean //Resume seul : ni liens d'action, ni marge pour les mesures en dessous
  onAddPrevention?: () => void
  onRiskClick?: () => void
  risk: PhysicalRisk
}

export default function RiskHeader({ isSummary, onAddPrevention, onRiskClick, risk }: Props) {
  const navigate = useNavigate()
  //Le 2e attribut sert de sous-titre a la carte : le risque formate porte deja sa valeur
  const descriptionAttribute = risk.cAttributes?.[1]
  const { getPath, isReadonly } = useRisksSpace()
  const { familyId, populationId, siteId } = useParams()
  const sites = useAppSelector((state) => siteStore.selectors.listForRisk(state, risk))
  const populations = useAppSelector((state) => populationStore.selectors.listForRisk(state, risk))
  const establishments = useAppSelector(establishmentStore.selectors.list)
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const visibleSites = sites.filter((site) => site.id !== siteId)
  const visiblePopulations = populations.filter(
    (population) => populations.length > 1 || population.id !== populationId
  )
  const familyImageUrl =
    risk.cFamily && risk.cFamily.id !== familyId ? getRiskFamilyImageUrl(risk.cFamily) : null
  const establishment = establishments.find((item) => item.id === risk.EstablishmentId) ?? activeEstablishment
  const hasNoSelectedUnit = sites.length === 0 && populations.length === 0
  const hasVisiblePerimeter =
    visibleSites.length > 0 || visiblePopulations.length > 0 || Boolean(hasNoSelectedUnit && establishment?.name)
  const isOnlyActivePopulation = populations.length === 1 && populations[0].id === populationId
  const showPeopleCount = risk.cSize > 0 && !isOnlyActivePopulation
  const hasPerimeterSummary = showPeopleCount || hasVisiblePerimeter

  function handleUnitClick(type: "site" | "population", id: string) {
    navigate(getPath(`/${type === "site" ? "sites" : "populations"}/${id}`))
  }

  const perimeterChips = (
    <div className="flex min-w-0 items-start">
      {showPeopleCount && (
        <div className="flex w-[46px] shrink-0 items-center px-[4px] py-[5px]">
          <PeopleCount count={risk.cSize} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-wrap gap-10">
        {hasNoSelectedUnit && establishment?.name && (
          <UnitChip unit={{ type: "establishment", name: establishment.name }} onClick={() => navigate(isReadonly ? getPath("") : "/home")} />
        )}
        {visibleSites.map((site) => (
          <UnitChip key={site.id} unit={{ type: "site", site }} onClick={() => handleUnitClick("site", site.id)} />
        ))}
        {visiblePopulations.map((population) => (
          <UnitChip
            key={population.id}
            unit={{ type: "population", population }}
            onClick={() => handleUnitClick("population", population.id)}
          />
        ))}
      </div>
    </div>
  )

  return (
    <div>
      <div className="flex items-start">
        {familyImageUrl && (
          <img
            alt={risk.cFamily?.name ?? ""}
            className="mr-[10px] h-[44px] w-[44px] shrink-0 object-contain"
            src={familyImageUrl}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-[20px]">
            <button
              type="button"
              className={`flex min-w-0 flex-1 items-center border-0 bg-transparent p-0 text-left ${
                onRiskClick ? "_hover" : ""
              }`}
              onClick={onRiskClick}>
              <div className="mr-20 shrink-0">
                <PhysicalRiskMeter risk={risk} size="sm" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate whitespace-nowrap text-lg" title={risk.cName}>
                  {risk.cName}
                </div>
                {descriptionAttribute && (
                  <div className="grey-t truncate whitespace-nowrap text-base font-normal">
                    {descriptionAttribute.value || "Non renseigné"}
                  </div>
                )}
              </div>
            </button>
            {!isReadonly && !isSummary && (onRiskClick || onAddPrevention) && (
              <div className="ml-auto mt-[4px] flex shrink-0 items-center gap-20">
                {onRiskClick && (
                  <Link icon={faPencilAlt} isWithoutMargin onClick={onRiskClick}>
                    Modifier risque
                  </Link>
                )}
                {onAddPrevention && (
                  <Link icon={faPlus} isWithoutMargin onClick={onAddPrevention}>
                    Ajouter mesure
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {hasPerimeterSummary && (
        <div className={`${isSummary ? "" : "mb-[20px] "}min-w-0 ${descriptionAttribute ? "mt-[15px]" : "mt-[20px]"}`}>
          {perimeterChips}
        </div>
      )}
      {!hasPerimeterSummary && !isSummary && <div aria-hidden="true" className="h-[10px]" />}
    </div>
  )
}
